import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import authService from '../services/auth-service';

const EmailVerificationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'success' | 'error' | 'expired'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setVerificationStatus('error');
        setErrorMessage('No verification token provided');
        return;
      }

      try {
        const success = await authService.verifyEmail(token);
        if (success) {
          setVerificationStatus('success');
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          setVerificationStatus('error');
          setErrorMessage('Verification failed. Please try again or contact support.');
        }
      } catch (error) {
        setVerificationStatus('error');
        setErrorMessage('Verification failed. Please try again or contact support.');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleResendVerification = async () => {
    try {
      // Get email from current user or redirect to login
      const currentUser = authService.getUser();
      if (!currentUser) {
        navigate('/login');
        return;
      }

      await authService.resendVerification(currentUser.email);
      setVerificationStatus('success');
      setErrorMessage('Verification email sent successfully!');
    } catch (error) {
      setErrorMessage('Failed to resend verification email. Please try again.');
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case 'verifying':
        return (
          <div className="verification-loading">
            <div className="spinner"></div>
            <h2>Verifying your email...</h2>
            <p>Please wait while we verify your email address.</p>
          </div>
        );

      case 'success':
        return (
          <div className="verification-success">
            <div className="success-icon">✅</div>
            <h2>Email Verified Successfully!</h2>
            <p>Your email has been verified. You now have full access to all features.</p>
            <p>Redirecting to dashboard...</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Go to Dashboard
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="verification-error">
            <div className="error-icon">❌</div>
            <h2>Verification Failed</h2>
            <p>{errorMessage}</p>
            <div className="verification-actions">
              <button onClick={handleResendVerification} className="btn-secondary">
                Resend Verification Email
              </button>
              <button onClick={handleGoToLogin} className="btn-primary">
                Go to Login
              </button>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="verification-expired">
            <div className="expired-icon">⏰</div>
            <h2>Verification Link Expired</h2>
            <p>Your verification link has expired. Please request a new one.</p>
            <div className="verification-actions">
              <button onClick={handleResendVerification} className="btn-primary">
                Request New Verification Email
              </button>
              <button onClick={handleGoToLogin} className="btn-secondary">
                Go to Login
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="email-verification-page">
      <div className="verification-container">
        <div className="verification-header">
          <h1>Email Verification</h1>
          <p>Larry's Help</p>
        </div>
        
        <div className="verification-content">
          {renderContent()}
        </div>

        <div className="verification-footer">
          <p>Need help? <a href="mailto:support@larryshelp.org">Contact Support</a></p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage; 